import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useApiClient } from "../api/ApiContext.jsx";
import { setAccountTimeZone } from "../ui/instant.js";

const AuthContext = createContext(null);

/**
 * Session bootstrap: one `GET /auth/session` on load.
 *   status "loading" -> render the minimal loading state,
 *   status "ready"   -> `user` is null (anonymous) or the `user` object.
 *
 * The client captures `csrf_token` from the session response itself, so this
 * provider only owns the user.
 */
export function AuthProvider({ children }) {
  const api = useApiClient();
  const [state, setState] = useState({ status: "loading", user: null });

  useEffect(() => {
    let active = true;
    setState({ status: "loading", user: null });
    api.auth
      .session()
      .then((data) => {
        if (active) setState({ status: "ready", user: data?.user ?? null });
      })
      .catch(() => {
        // 401 here is the normal anonymous case, not an error screen.
        if (active) setState({ status: "ready", user: null });
      });
    return () => {
      active = false;
    };
  }, [api]);

  /*
   * Every screen prints instants, and they should read in the customer's zone
   * rather than in whatever the browser happens to be set to — the two differ
   * for anyone travelling, and the difference is exactly one hour of confusion
   * on a schedule chart.
   *
   * Driven off the user rather than set at each place a session is adopted, so
   * login, bootstrap, sign-out and a timezone change all travel the same path.
   */
  useEffect(() => {
    setAccountTimeZone(state.user?.display_timezone ?? null);
  }, [state.user]);

  // Any 401 outside /auth/* drops the session; guards then route to /login.
  useEffect(
    () =>
      api.setUnauthorizedHandler(() => {
        setState((prev) => (prev.user === null ? prev : { status: "ready", user: null }));
      }),
    [api],
  );

  /** Adopt a session returned by login / 2FA verify. */
  const adoptSession = useCallback((data) => {
    if (data?.csrf_token) api.setCsrfToken?.(data.csrf_token);
    setState({ status: "ready", user: data?.user ?? null });
    return data?.user ?? null;
  }, [api]);

  const signOut = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      /* Logout is best-effort: drop local state either way. */
    }
    api.setCsrfToken?.(null);
    setState({ status: "ready", user: null });
  }, [api]);

  const refresh = useCallback(async () => {
    const data = await api.auth.session();
    setState({ status: "ready", user: data?.user ?? null });
    return data?.user ?? null;
  }, [api]);

  const value = useMemo(
    () => ({
      status: state.status,
      user: state.user,
      isAuthenticated: Boolean(state.user),
      // The server reports the effective capability: the admin role *and* a
      // second factor. A role alone is not enough, so `can_admin` is what the
      // guard and the nav read.
      isAdmin: Boolean(state.user?.can_admin),
      adoptSession,
      signOut,
      refresh,
    }),
    [state.status, state.user, adoptSession, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}

export const useSession = useAuth;
